import { useMutation, useQuery } from "@apollo/client";
import {
	Anchor,
	Button,
	Card,
	Container,
	Group,
	Loader,
	ScrollArea,
	Stack,
	Table,
	Text,
	Title,
} from "@mantine/core";
import { IconCheck, IconUsers } from "@tabler/icons-react";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import type { GetWaitlistUsersQuery } from "../../graphql/generated/types.gen";
import {
	APPROVE_WAITLIST_USER,
	GET_WAITLIST_USERS,
} from "../../graphql/queries";
import { useAuthCompat } from "../../hooks/useAuthCompat";
import { useIsMobile } from "../../hooks/useIsMobile";

type WaitlistUser = GetWaitlistUsersQuery["waitlistUsers"][number];

export const Route = createFileRoute("/admin/waitlist")({
	component: AdminWaitlistPage,
});

function AdminWaitlistPage() {
	const { user, isLoading: authLoading } = useAuthCompat();

	useEffect(() => {
		if (authLoading) return;
		if (!user) {
			window.location.href = "/welcome";
			return;
		}
		if (!user.isEmployee) {
			window.location.href = "/";
		}
	}, [user, authLoading]);

	const { data, loading, refetch } = useQuery(GET_WAITLIST_USERS, {
		skip: !user?.isEmployee,
	});

	const [approveUser, { loading: approving }] = useMutation(
		APPROVE_WAITLIST_USER,
		{
			onCompleted: () => refetch(),
			onError: (error) => {
				console.error("Failed to approve user:", error.message);
				alert(`Failed to approve user: ${error.message}`);
			},
		},
	);

	const isMobile = useIsMobile();

	if (authLoading || loading || !user?.isEmployee) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<Loader size="lg" />
			</div>
		);
	}

	const waitlistUsers = data?.waitlistUsers || [];

	return (
		<div className="h-[calc(100vh-var(--header-height))] overflow-auto bg-[var(--mantine-color-body)]">
			<Container size="lg" py="xl" px={isMobile ? "md" : "xl"}>
				<Group gap="md" mb="xs" wrap="nowrap">
					<IconUsers
						size={isMobile ? 24 : 32}
						color="var(--mantine-color-blue-6)"
					/>
					<Title order={isMobile ? 2 : 1}>Waitlist Management</Title>
				</Group>
				<div className="mb-6">
					<Anchor href="/admin">&larr; Back to Admin</Anchor>
				</div>

				{waitlistUsers.length === 0 ? (
					<Card shadow="sm" padding="xl" radius="md" withBorder>
						<Text size="lg" c="dimmed" ta="center">
							No users on the waitlist.
						</Text>
					</Card>
				) : isMobile ? (
					<Stack gap="md">
						{waitlistUsers.map((user: WaitlistUser) => (
							<Card
								key={user.id}
								shadow="sm"
								padding="md"
								radius="md"
								withBorder
							>
								<Stack gap="xs">
									<Text fw={600}>{user.displayName}</Text>
									<Text size="sm" c="dimmed" style={{ wordBreak: "break-all" }}>
										{user.email}
									</Text>
									<Group justify="space-between" align="center">
										<Text size="xs" c="dimmed">
											{new Date(user.createdAt).toLocaleDateString()}
										</Text>
										<Button
											size="xs"
											color="green"
											leftSection={<IconCheck size={14} />}
											loading={approving}
											onClick={() =>
												approveUser({ variables: { userId: user.id } })
											}
										>
											Approve
										</Button>
									</Group>
								</Stack>
							</Card>
						))}
					</Stack>
				) : (
					<Card shadow="sm" padding={0} radius="md" withBorder>
						<ScrollArea>
							<Table striped highlightOnHover>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>Name</Table.Th>
										<Table.Th>Email</Table.Th>
										<Table.Th>Signed Up</Table.Th>
										<Table.Th>Actions</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{waitlistUsers.map((user: WaitlistUser) => (
										<Table.Tr key={user.id}>
											<Table.Td>{user.displayName}</Table.Td>
											<Table.Td>{user.email}</Table.Td>
											<Table.Td>
												{new Date(user.createdAt).toLocaleDateString()}
											</Table.Td>
											<Table.Td>
												<Button
													size="xs"
													color="green"
													leftSection={<IconCheck size={14} />}
													loading={approving}
													onClick={() =>
														approveUser({ variables: { userId: user.id } })
													}
												>
													Approve
												</Button>
											</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						</ScrollArea>
					</Card>
				)}
			</Container>
		</div>
	);
}
